import React, { useState, useEffect } from "react";
import { bookingDetail } from "../../services/bookingServices";
import { useParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { formatApiDate, getApiCalendarParts } from "../../utils/dateHelper";

const ViewBookingPageNew = ({ bookingId }) => {
  const { id: routeBookingId } = useParams();
  const id = bookingId || routeBookingId;
  const [item, setItem] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await bookingDetail(id);
      console.log("Booking Detail Response:", response);
      setItem(response?.data?.booking || null);
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to fetch booking detail");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const formatDate = (dateStr) =>
    formatApiDate(dateStr, { separator: "/", fallback: "" });

  // ✅ Helper to format price (handles both number and object with day keys)
  const formatPrice = (price, prefix = "$") => {
    if (price == null) return "N/A";
    if (typeof price === "number") return `${prefix}${price}`;
    if (typeof price === "object" && !Array.isArray(price)) {
      const entries = Object.entries(price)
        .filter(([, v]) => v != null)
        .map(([day, val]) => {
          const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
          return `${dayLabel}: ${prefix}${val}`;
        });
      return entries.length > 0 ? entries.join(", ") : "N/A";
    }
    return String(price);
  };

  const getNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatAmount = (value) => getNumber(value).toFixed(2);

  const getPromoterCommissionType = (type) => {
    const raw = type ?? "";
    const normalized = String(raw).toLowerCase().replace(/\s+/g, "");
    if (normalized.includes("flat")) return "flatfee";
    if (normalized.includes("percent")) return "percentage";
    return normalized;
  };

  const formatPromoterCommissionValue = (value, type) => {
    if (value == null || value === "") return "N/A";
    const num = Number(value);
    if (!Number.isFinite(num)) return "N/A";
    const formatted = Number.isInteger(num)
      ? String(num)
      : num.toFixed(2).replace(/\.0+$/, "");
    return type === "flatfee" ? `$${formatted}` : `${formatted}%`;
  };

  const getDiscountType = (booking) => {
    const raw =
      booking?.discountType ??
      booking?.discountype ??
      booking?.discount_type ??
      booking?.promoDiscountType ??
      booking?.couponType ??
      "";
    const normalized = String(raw).toLowerCase().replace(/\s+/g, "");
    if (normalized.includes("flat")) return "flatfee";
    if (normalized.includes("percent")) return "percentage";
    return normalized || "";
  };

  const formatDiscountTypeLabel = (type) => {
    if (type === "flatfee") return "Flat Fee";
    if (type === "percentage") return "Percentage";
    return "N/A";
  };

  const formatPromoterPayout = (value) => {
    if (value == null || value === "") return "N/A";
    const num = Number(value);
    if (!Number.isFinite(num)) return "N/A";
    return `$${num.toFixed(2)}`;
  };

  // Supports multiple backend shapes for drink price fields
  const getDrinkLinePrice = (drink) => {
    if (!drink || typeof drink !== "object") return 0;
    if (drink.totalPrice != null) return getNumber(drink.totalPrice);

    const quantity = getNumber(drink.quantity);
    if (drink.price != null) return getNumber(drink.price) * (quantity || 1);
    if (drink.bottlePrice != null) return getNumber(drink.bottlePrice) * (quantity || 1);
    if (drink.reservationFee != null) return getNumber(drink.reservationFee) * (quantity || 1);
    return 0;
  };

  const getDrinkTotals = (list) => {
    if (!Array.isArray(list) || list.length === 0) return { quantity: 0, price: 0 };
    return list.reduce(
      (acc, drink) => ({
        quantity: acc.quantity + getNumber(drink?.quantity),
        price: acc.price + getDrinkLinePrice(drink),
      }),
      { quantity: 0, price: 0 }
    );
  };

  const getBookingDaysSet = () => {
    if (!item?.bookingStartDate || !item?.bookingEndDate) return new Set();
    const start = getApiCalendarParts(item.bookingStartDate);
    const end = getApiCalendarParts(item.bookingEndDate);
    if (!start || !end) return new Set();
    const cursor = new Date(
      Date.UTC(Number(start.year), Number(start.month) - 1, Number(start.day))
    );
    const last = new Date(
      Date.UTC(Number(end.year), Number(end.month) - 1, Number(end.day))
    );
    const days = new Set();
    const weekdayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    while (cursor <= last) {
      days.add(weekdayNames[cursor.getUTCDay()]);
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return days;
  };

  const bookingDays = getBookingDaysSet();

  const getDrinkRate = (drink, type) => {
    const pricingList = item?.drinkPricing?.[type] || [];
    const drinkId = drink?.BottleServiceId || drink?.bottleServiceId;
    const pricingItem = pricingList.find(
      (p) =>
        (drinkId && (p?.BottleServiceId === drinkId || p?.bottleServiceId === drinkId)) ||
        p?.name === drink?.name
    );

    if (!pricingItem?.dateWisePricing?.length) {
      return { reservationFee: 0, bottlePrice: 0 };
    }

    const inBookingRange = pricingItem.dateWisePricing.filter((d) =>
      bookingDays.has((d?.day || "").toLowerCase())
    );
    const preferredFromRange =
      inBookingRange.find((d) => getNumber(d?.reservationFee) > 0 || getNumber(d?.bottlePrice) > 0) ||
      inBookingRange[0];

    const fallbackAny =
      pricingItem.dateWisePricing.find(
        (d) => getNumber(d?.reservationFee) > 0 || getNumber(d?.bottlePrice) > 0
      ) || pricingItem.dateWisePricing[0];

    const selected = preferredFromRange || fallbackAny;
    return {
      reservationFee: getNumber(selected?.reservationFee),
      bottlePrice: getNumber(selected?.bottlePrice),
    };
  };

  const getBoothId = (booking) => {
    const raw =
      booking?.boothId ??
      booking?.booth_id ??
      booking?.booth?._id ??
      booking?.boothData?._id;
    if (!raw) return "";
    if (typeof raw === "object") return String(raw._id || raw.id || "").trim();
    return String(raw).trim();
  };

  const getBoothData = (booking) => {
    const populated =
      booking?.boothId && typeof booking.boothId === "object"
        ? booking.boothId
        : null;
    const fromField = booking?.boothData || booking?.booth || populated;
    const boothId = getBoothId(booking);
    const lists = [
      booking?.eventData?.booths,
      booking?.clubData?.booths,
      booking?.booths,
    ].filter(Array.isArray);

    for (const list of lists) {
      const found = list.find(
        (b) => String(b?._id || b?.id || "").trim() === boothId
      );
      if (found) return found;
    }

    return fromField || null;
  };

  const boothId = getBoothId(item);
  const boothData = getBoothData(item);
  const showBoothData = Boolean(boothId || boothData);

  const alcoholicTotals = getDrinkTotals(item?.drinks?.alcoholic);
  const nonAlcoholicTotals = getDrinkTotals(item?.drinks?.nonAlcoholic);
  const alcoholicQuantity = item?.alcoholicQuantity ?? alcoholicTotals.quantity;
  const alcoholicPrice = item?.alcoholicPrice ?? alcoholicTotals.price;
  const nonAlcoholicQuantity =
    item?.nonalcoholicQuantity ?? nonAlcoholicTotals.quantity;
  const nonAlcoholicPrice = item?.nonalcoholicPrice ?? nonAlcoholicTotals.price;

  return (
    <div className="row gy-4">
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            {loading ? (
              <p>Loading...</p>
            ) : item ? (
              <>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <p>
                      <strong>User Name:</strong>{" "}
                      {item?.fullName || item?.userData?.fullName || "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Email:</strong>{" "}
                      {item?.email || item?.userData?.email || "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Age:</strong> {item?.age ?? item?.userData?.age ?? "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      {item?.phoneNumber ||
                        item?.userData?.phoneNumber ||
                        item?.userData?.mobileNumber ||
                        item?.userData?.phone ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Event:</strong> {item?.eventData?.name}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Host Name:</strong> {item?.hostData?.fullName}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Promoter Name:</strong>{" "}
                      {item?.promoterData?.fullName || item?.promoterData?.name || item?.promoterName || "N/A"}
                    </p>
                  </div>
                  {/* <div className="col-md-3">
                    <p>
                      <strong>Promoter Email:</strong>{" "}
                      {item?.promoterData?.email || item?.promoterEmail || "N/A"}
                    </p>
                  </div> */}
                  <div className="col-md-3">
                    <p>
                      <strong>Booking Created Date:</strong>{" "}
                      {formatDate(item.createdAt)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Booking Start Date:</strong>{" "}
                      {formatDate(item.bookingStartDate)}
                    </p>
                  </div>
                  
                  <div className="col-md-3">
                    <p>
                      <strong>Booking End Date:</strong>{" "}
                      {formatDate(item.bookingEndDate)}
                    </p>
                  </div>
                   <div className="col-md-3">
                    <p>
                      <strong>Gratuity($):</strong>{" "}
                      {formatAmount(item.gratuityAmount)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Members:</strong> {item.members}
                    </p>
                  </div>
                </div>
                <div className="row">
                  {showBoothData && (
                    <>
                      <div className="col-md-3">
                        <p>
                          <strong>Booth Name:</strong>{" "}
                          {boothData?.boothName ||
                            boothData?.name ||
                            item?.boothName ||
                            "N/A"}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p>
                          <strong>Booth Type:</strong>{" "}
                          {boothData?.boothTypeName ||
                            boothData?.boothType ||
                            boothData?.type ||
                            "N/A"}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p>
                          <strong>Booth Capacity:</strong>{" "}
                          {boothData?.capacity ?? "N/A"}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p>
                          <strong>Booth Price:</strong>{" "}
                          {formatPrice(
                            boothData?.boothPrice ??
                              boothData?.price ??
                              item?.boothCost,
                            "$"
                          )}
                        </p>
                      </div>
                      {(boothData?.discountedPrice != null ||
                        boothData?.discountPrice != null) && (
                        <div className="col-md-3">
                          <p>
                            <strong>Booth Discounted Price:</strong>{" "}
                            {formatPrice(
                              boothData?.discountedPrice ??
                                boothData?.discountPrice,
                              "$"
                            )}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  <div className="col-md-3">
                    <p>
                      <strong>Booth Cost:</strong> ${formatAmount(item.boothCost)}
                    </p>
                  </div>

                  <div className="col-md-3">
                    <p>
                      <strong>Ticket Cost:</strong> {formatAmount(item.ticketCost)}
                    </p>
                  </div>
                  {/* <div className="col-md-3">
                    <p>
                      <strong>Ticket Type:</strong> {item.ticketType}
                    </p>
                  </div> */}
                  <div className="col-md-3">
                    <p>
                      <strong>Discount ($):</strong>{" "}
                      {formatAmount(item.discount)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Discount Type:</strong>{" "}
                      {formatDiscountTypeLabel(getDiscountType(item))}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Discount Code:</strong>{" "}
                      {item?.discountCode ||
                        item?.discount_code ||
                        item?.couponCode ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Total Amount:</strong> ${formatAmount(item.totalAmount)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Promoter Commission:</strong>{" "}
                      {formatPromoterCommissionValue(
                        item?.promotercommissionValue ?? item?.promoterCommissionValue,
                        getPromoterCommissionType(
                          item?.promotercommissionType ?? item?.promoterCommissionType
                        )
                      )}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Promoter Payout:</strong>{" "}
                      {formatPromoterPayout(item?.promoterPayout)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Admin Comission ($):</strong>{" "}
                      {formatAmount(item.adminFee)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Admin Taxes: </strong> $
                      {formatAmount(item.adminHST || item.fees)}
                    </p>
                  </div>
                  

                  <div className="col-md-3">
                    <p>
                      <strong>Admin Payout:(Admin Taxes + Admin Comission) </strong> $
                      {formatAmount(item.adminPayout)}
                    </p>
                  </div>
                    <div className="col-md-3">
                    <p>
                      <strong>Host Taxes:</strong> $
                      {formatAmount(item.hostHST || 0)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Host Payout:</strong> $
                      {formatAmount(item.hostPayout)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Total Taxes:</strong> ${formatAmount(item.fees)}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Bookedby:</strong>  {item.bookedby ?? 'user'}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p>
                      <strong>Status:</strong> {item.status}
                    </p>
                  </div>

                  <div className="col-md-12 mt-3">
                    <strong className="d-block mb-3">Drinks</strong>
                    <div className="row g-3 mb-3">
                      <div className="col-md-3">
                        <p className="mb-1">
                          <strong>Alcoholic Quantity:</strong> {alcoholicQuantity}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-1">
                          <strong>Alcoholic Price:</strong> ${formatAmount(alcoholicPrice)}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-1">
                          <strong>Non Alcoholic Quantity:</strong> {nonAlcoholicQuantity}
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-1">
                          <strong>Non Alcoholic Price:</strong> ${formatAmount(nonAlcoholicPrice)}
                        </p>
                      </div>
                    </div>
                    <div className="row g-3">
                      {item?.drinks?.alcoholic?.length > 0 && (
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100">
                            <strong className="d-block mb-2 text-secondary">
                              Alcoholic
                            </strong>
                            <table className="table table-sm table-borderless mb-0">
                              <thead>
                                <tr>
                                  <th className="fw-semibold">Name</th>
                                  <th className="fw-semibold text-end">Qty</th>
                                  <th className="fw-semibold text-end">Reserved Price</th>
                                  <th className="fw-semibold text-end">Bottle Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.drinks.alcoholic.map((d, i) => {
                                  const rate = getDrinkRate(d, "alcoholic");
                                  return (
                                    <tr key={i}>
                                      <td>{d.name}</td>
                                      <td className="text-end">{d.quantity}</td>
                                      <td className="text-end">${formatAmount(rate.reservationFee)}</td>
                                      <td className="text-end">${formatAmount(rate.bottlePrice)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {item?.drinks?.nonAlcoholic?.length > 0 && (
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100">
                            <strong className="d-block mb-2 text-secondary">
                              Non Alcoholic
                            </strong>
                            <table className="table table-sm table-borderless mb-0">
                              <thead>
                                <tr>
                                  <th className="fw-semibold">Name</th>
                                  <th className="fw-semibold text-end">Qty</th>
                                  <th className="fw-semibold text-end">Reserved Price</th>
                                  <th className="fw-semibold text-end">Bottle Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.drinks.nonAlcoholic.map((d, i) => {
                                  const rate = getDrinkRate(d, "nonAlcoholic");
                                  return (
                                    <tr key={i}>
                                      <td>{d.name}</td>
                                      <td className="text-end">{d.quantity}</td>
                                      <td className="text-end">${formatAmount(rate.reservationFee)}</td>
                                      <td className="text-end">${formatAmount(rate.bottlePrice)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {item?.type === "table" && (
                    <>
                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Total Male:
                        </label>
                        {item?.maleCount ?? "N/A"}
                      </div>

                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Total Female:
                        </label>
                        {item?.femaleCount ?? "N/A"}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Female Price:
                        </label>
                        {formatPrice(item?.eventData?.femalePrice)}
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Male Price:
                        </label>
                        {formatPrice(item?.eventData?.malePrice)}
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p>No data found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBookingPageNew;
